// ─── Validate-tab API helpers ────────────────────────────────────────────────
// Talks to the Django endpoints introduced in Phase A/B (see
// wqlive/app_data/validation_views.py):
//
//   POST /api/data/marks/apply/   → create a rule + tag matching Data rows
//   GET  /api/data/marks/         → load marks for a parameter + range
//   GET  /api/data/rules/         → list rules ever created for a parameter
//
// Same JWT-in-localStorage / Bearer-header convention as projectsApi.js.
// ─────────────────────────────────────────────────────────────────────────────

import axios from 'axios'

const API_URL         = import.meta.env.VITE_API_URL          || 'https://wqlive.ezxdemo.com'
const API_BASE_PATH   = import.meta.env.VITE_API_BASE_PATH    || '/api'
const JWT_STORAGE_KEY = import.meta.env.VITE_JWT_STORAGE_KEY  || 'auth_token'

const authHeaders = () => {
  const token = (localStorage.getItem(JWT_STORAGE_KEY) || sessionStorage.getItem(JWT_STORAGE_KEY))
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  }
}

// GET helpers go through this so /marks/, /rules/, /comments/list/ always
// hit the server — without `no-cache` the browser would serve stale colour
// data even after rows were deleted on the backend, which made cleared
// marks "stick" in one browser while disappearing in another.
const getHeaders = () => ({
  ...authHeaders(),
  'Cache-Control': 'no-cache, no-store, must-revalidate',
  'Pragma':        'no-cache',
  'Expires':       '0',
})

const url = (p) => `${API_URL}${API_BASE_PATH}/data${p}`

/**
 * POST /api/data/marks/apply/
 * Backend creates a ValidationRule and stamps `rule_id` on every matching
 * Data row, never touching `original_value` or `value`.  Sync-only for now
 * (Phase C will add async); on `too_large` (HTTP 413) the caller should
 * tell the user to narrow the date range.
 *
 *   args: { parameterId, condition, v1, v2?, status,
 *           fromDate?, toDate?, comment? }
 *   returns: { rule_id, affected_rows, status }
 *   throws:  { kind: 'too_large', estimated_count, message } |
 *            { kind: 'bad_request' | 'http', status, message }
 */
export async function applyMarkRule (args) {
  const body = {
    parameter_id: args.parameterId,
    condition:    args.condition,
    v1:           args.v1 ?? null,
    v2:           args.v2 ?? null,
    status:       args.status,
    from_date:    args.fromDate || null,
    to_date:      args.toDate   || null,
    timezone:     args.timezone || null,   // IANA tz so the backend can interpret from/to_date correctly
    comment:      args.comment  || null,
  }
  try {
    const res = await axios.post(url('/marks/apply/'), body, { headers: authHeaders() })
    return res.data
  } catch (err) {
    const status = err.response?.status
    const data   = err.response?.data || {}
    if (status === 413 && data.error === 'too_large') {
      throw { kind: 'too_large', estimated_count: data.estimated_count, message: data.message }
    }
    if (status === 400) {
      throw { kind: 'bad_request', status, message: data.message || 'Invalid request' }
    }
    throw { kind: 'http', status: status || 0, message: data.message || err.message || 'Network error' }
  }
}

/** GET /api/data/marks/?parameter_id=…&from_date=…&to_date=…
 *  Returns: { marks: [{data_id, creation_time, original_value, value, rule_id, status}, ...] } */
export async function fetchMarks ({ parameterId, fromDate, toDate, timezone }) {
  const params = { parameter_id: parameterId, _t: Date.now() }
  if (fromDate) params.from_date = fromDate
  if (toDate)   params.to_date   = toDate
  if (timezone) params.timezone  = timezone   // IANA tz; backend converts from/to to UTC using this
  const res = await axios.get(url('/marks/'), { headers: getHeaders(), params })
  return res.data
}

/** GET /api/data/rules/?parameter_id=…[&rule_type=one_time|on_going][&include_disabled=true]
 *  Returns: { rules: [{id, condition, v1, v2, status, ..., rule_type, enabled, affected_rows}, ...] } */
export async function fetchRules ({ parameterId, ruleType = null, includeDisabled = false }) {
  const params = { parameter_id: parameterId }
  if (ruleType)         params.rule_type        = ruleType
  if (includeDisabled)  params.include_disabled = 'true'
  const res = await axios.get(url('/rules/'), { headers: getHeaders(), params })
  return res.data
}

/** POST /api/data/rules/create/ — create an ON-GOING rule (Mark Rules modal).
 *  Does NOT touch existing Data; the cron picks it up on its next 5-min tick.
 *  Body: { parameterId, condition, v1, v2?, status, comment? }
 *  Returns: the newly created rule object. */
export async function createOnGoingRule (args) {
  const body = {
    parameter_id: args.parameterId,
    name:         args.name || null,
    condition:    args.condition,
    v1:           args.v1 ?? null,
    v2:           args.v2 ?? null,
    status:       args.status,
    comment:      args.comment || null,
  }
  const res = await axios.post(url('/rules/create/'), body, { headers: authHeaders() })
  return res.data
}

/** PATCH /api/data/rules/<id>/ — partial update (toggle enabled, edit fields). */
export async function patchRule (id, patch) {
  const res = await axios.patch(url(`/rules/${id}/`), patch, { headers: authHeaders() })
  return res.data
}

/** DELETE /api/data/rules/<id>/ — soft delete (sets enabled=false; keeps audit). */
export async function deleteRule (id) {
  const res = await axios.delete(url(`/rules/${id}/`), { headers: authHeaders() })
  return res.data
}

/** POST /api/data/cells/update/ — overwrite Data.value for selected cells.
 *  `original_value` is left untouched (hard backend invariant).
 *  Args: { parameterId, sampleTimes:[], newValue, status?, timezone?, comment? }
 *  Returns: { rule_id, affected_rows, status, new_value } */
export async function updateCellValues (args) {
  const body = {
    parameter_id: args.parameterId,
    sample_times: args.sampleTimes || [],
    new_value:    args.newValue,
    status:       args.status  || 'corrected',
    timezone:     args.timezone || null,
    comment:      args.comment  || null,
  }
  const res = await axios.post(url('/cells/update/'), body, { headers: authHeaders() })
  return res.data
}

/** POST /api/data/marks/apply-cells/ — tag a hand-picked list of cells with a status.
 *  Used by the grid's "select then click pill → Mark selected as X" flow.
 *  Args: { parameterId, status, sampleTimes:[], timezone, comment? }
 *  Returns: { rule_id, affected_rows, status } */
export async function applyMarkByCells (args) {
  const body = {
    parameter_id: args.parameterId,
    status:       args.status,
    sample_times: args.sampleTimes || [],
    timezone:     args.timezone || null,
    comment:      args.comment  || null,
  }
  const res = await axios.post(url('/marks/apply-cells/'), body, { headers: authHeaders() })
  return res.data
}

// ─── Cell comments (server-persisted + multimedia) ──────────────────────────
//
//   POST   /api/data/comments/         create (multipart)
//   GET    /api/data/comments/list/    list for parameter + range
//   DELETE /api/data/comments/<id>/    soft delete
//
// Files (image/video) are uploaded as multipart and stored on disk under
// MEDIA_ROOT/comments/<id>/; the server returns absolute URLs that <img>
// can use directly via the existing /media/ nginx location.

/** POST /api/data/comments/ — create one or more comments.
 *  args: {
 *    parameterId,
 *    // Either ONE of these — sampleTimes[] is preferred for multi-select.
 *    sampleTime?, cellRef?,
 *    sampleTimes?: string[],          // 'YYYY-MM-DD HH:MM:SS' per cell
 *    cellRefs?:    string[],          // matching A1-style label per cell
 *    body,
 *    files?: File[],
 *    timezone?,
 *  }
 *  Server creates one Comment row per sample_time, attaching the same body
 *  + the same uploaded file set to each row. Returns the single row shape
 *  for one cell, or `{ comments: [...] }` for many.
 */
export async function createComment (args) {
  const fd = new FormData()
  fd.append('parameter_id', String(args.parameterId))
  // Multi-cell — append each sample_time / cell_ref as a repeated form key.
  if (Array.isArray(args.sampleTimes) && args.sampleTimes.length > 0) {
    for (const s of args.sampleTimes) fd.append('sample_times', String(s))
    if (Array.isArray(args.cellRefs)) {
      for (const r of args.cellRefs) fd.append('cell_refs', String(r || ''))
    }
  } else if (args.sampleTime) {
    fd.append('sample_time', String(args.sampleTime))
    if (args.cellRef) fd.append('cell_ref', String(args.cellRef))
  }
  fd.append('body', String(args.body || ''))
  if (args.timezone) fd.append('timezone', String(args.timezone))
  for (const f of (args.files || [])) fd.append('attachments', f, f.name)
  // Don't set Content-Type — let the browser pick the multipart boundary.
  const headers = { ...authHeaders() }
  delete headers['Content-Type']
  const res = await axios.post(url('/comments/'), fd, { headers })
  return res.data
}

/** GET /api/data/comments/list/?parameter_id=…&from_date=…&to_date=…&timezone=…
 *  returns: { comments: [{id, parameter_id, sample_time, cell_ref, body,
 *             created_by, created_at, attachments:[{id, filename, mime_type, size_bytes, url}]}]} */
export async function fetchComments ({ parameterId, fromDate, toDate, timezone }) {
  const params = { parameter_id: parameterId }
  if (fromDate) params.from_date = fromDate
  if (toDate)   params.to_date   = toDate
  if (timezone) params.timezone  = timezone
  const res = await axios.get(url('/comments/list/'), { headers: getHeaders(), params })
  return res.data
}

/** DELETE /api/data/comments/<id>/ — soft delete (sets deleted_at). */
export async function deleteComment (id) {
  const res = await axios.delete(url(`/comments/${id}/`), { headers: authHeaders() })
  return res.data
}

// ─── Per-project validation-status visibility (the Validate Show/Hide pills) ──
// GET/PUT /api/project/<projectId>/validation-visibility/ — a SHARED rule: the
// hidden statuses are excluded everywhere the project's data is read.
const projectUrl = (projectId, p) => `${API_URL}${API_BASE_PATH}/project/${projectId}${p}`

/** GET → { hidden_statuses: [...] } */
export async function fetchValidationVisibility (projectId) {
  const res = await axios.get(projectUrl(projectId, '/validation-visibility/'), { headers: getHeaders() })
  return res.data
}

/** PUT the full hidden-status list. Returns { ok, hidden_statuses }. */
export async function saveValidationVisibility (projectId, hiddenStatuses) {
  const res = await axios.put(
    projectUrl(projectId, '/validation-visibility/'),
    { hidden_statuses: hiddenStatuses },
    { headers: authHeaders() },
  )
  return res.data
}

export default {
  applyMarkRule, applyMarkByCells, updateCellValues, fetchMarks, fetchRules,
  createOnGoingRule, patchRule, deleteRule,
  createComment, fetchComments, deleteComment,
  fetchValidationVisibility, saveValidationVisibility,
}
